/**
 * Small numeric helpers shared across the game.
 *
 * Angle conventions used throughout this project:
 *  - Internal angle maths is always in RADIANS.
 *  - Layout specs (data files) use DEGREES for readability.
 *  - Board angles are measured counter-clockwise from +X, so 90 deg is "up"
 *    (the top of the board) and 270 deg is "down" (the bottom).
 */

export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;
export const TWO_PI = Math.PI * 2;

export function degToRad(deg: number): number {
  return deg * DEG2RAD;
}

export function radToDeg(rad: number): number {
  return rad * RAD2DEG;
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Wrap an angle into [0, 2*PI).
 *
 * Only ever used for building a quaternion (which is inherently periodic).
 * The gameplay accumulator deliberately stays UNBOUNDED - see
 * `MazeAssembly.angleRad` - so that a player can keep spinning the board past
 * 360 degrees indefinitely.
 */
export function wrapAngle(rad: number): number {
  return ((rad % TWO_PI) + TWO_PI) % TWO_PI;
}

export interface QuatLike {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** Quaternion for a rotation of `angleRad` about a normalised axis. */
export function quatFromAxisAngle(
  ax: number,
  ay: number,
  az: number,
  angleRad: number,
): QuatLike {
  const half = angleRad / 2;
  const s = Math.sin(half);
  return { x: ax * s, y: ay * s, z: az * s, w: Math.cos(half) };
}

/**
 * Quaternion for a rotation of `angleRad` about the Z axis.
 *
 * Z is the maze's rotation axis (the board faces the player along Z), so this
 * is the single quaternion that drives the whole assembly.
 */
export function quatAboutZ(angleRad: number): QuatLike {
  const half = angleRad / 2;
  return { x: 0, y: 0, z: Math.sin(half), w: Math.cos(half) };
}

/** Step `current` toward `target` by at most `maxDelta`. */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}
